/**
 * Shared "what does the tenant have to do about their handover protocol"
 * logic (COL-368).
 *
 * `ProtocolEntry` (inside the Check-in/out tab) and the dashboard tab strip
 * (outside it) must agree on the same answer, otherwise a pulsing pip on the
 * tab leads to a card with nothing to do — or a card demands action with no
 * pip to lead there. Both read from here; the per-kind state machine used to
 * live inside `ProtocolEntry` and moved here unchanged.
 *
 * The window math itself stays in `protocol-window.ts` (shared with the
 * protocol-reminder-tick cron) — this file only interprets it.
 */
import {
  isAdminRecorded,
  isRealSubmission,
  isSupersededByReopen,
  type Protocol,
  type ProtocolKind,
} from "@/lib/protocol";
import {
  computeMoveInWindowStatus,
  computeMoveOutWindowStatus,
  type ProtocolWindowState,
} from "@/lib/protocol-window";

/**
 * How many days before the move the tenant is nagged in-app.
 *
 * These mirror the tenant lifecycle emails (`tenant-lifecycle-email-tick`):
 * the move-in details mail goes out the day before move-in, the move-out
 * upcoming mail seven days before move-out. The in-app pip appears from the
 * same moment, so the email and the dashboard tell one story. The pip is
 * date-derived rather than "email row exists" on purpose — the live cron is a
 * manual step, and the nag must not depend on delivery.
 */
export const MOVE_IN_ALERT_LEAD_DAYS = 1;
export const MOVE_OUT_ALERT_LEAD_DAYS = 7;

export type ProtocolBookingLite = {
  move_in_date?: string | null;
  move_out_date?: string | null;
  /**
   * False when `move_out_date` above is only the contract's `end_date`
   * borrowed as a fallback. `end_date` is the earliest POSSIBLE move-out on
   * an auto-prolonging contract, and nagging from it would demand a move-out
   * protocol nobody confirmed — the same trap the email chain (#1066/#1067)
   * and billing (#1333/#1336) already avoid. Omitted means "trust the date".
   */
  move_out_confirmed?: boolean;
  move_in_protocol_reopened_at?: string | null;
  move_out_protocol_reopened_at?: string | null;
  move_in_protocol_manually_closed_at?: string | null;
  move_out_protocol_manually_closed_at?: string | null;
};

export type KindStatus =
  | "submitted"
  | "draft"
  | "not_started"
  | "expired"
  | "closed";

export type KindState = {
  /**
   * - submitted   — protocol exists in `submitted` state (PDF available).
   * - draft       — protocol exists in `draft` state; user can resume while
   *                 the window is open.
   * - not_started — no protocol yet, but the window is open right now.
   * - expired     — for move-in, the 48h post-move-in window closed without
   *                 a submitted protocol. Room is auto-accepted.
   *   • "closed" — an admin manually closed a reopened protocol before the
   *                tenant submitted it. Distinct from "expired": nothing has
   *                been deemed accepted/completed (yet) — this is a neutral
   *                re-lock, not a legal consequence.
   */
  kindStatus: KindStatus;
  locked: boolean;
  submittedAt: string | null;
  ref: string | null;
  /** ISO date string when this kind becomes available, if currently locked. */
  availableFrom: string | null;
  /** The raw window state this was derived from. */
  windowState: ProtocolWindowState;
};

export function computeKindState(
  protocol: Protocol | null,
  booking: ProtocolBookingLite | null | undefined,
  kind: ProtocolKind,
  now: number = Date.now()
): KindState {
  const windowStatus =
    kind === "move_in"
      ? computeMoveInWindowStatus({
          moveInDate: booking?.move_in_date,
          moveInReopenedAt: booking?.move_in_protocol_reopened_at,
          now,
        })
      : computeMoveOutWindowStatus({
          moveOutDate: booking?.move_out_date,
          moveOutReopenedAt: booking?.move_out_protocol_reopened_at,
          now,
        });
  // Set only until protocol-reminder-tick's next run deems this submitted
  // (or an admin reopens it again, which clears the stamp) — see
  // 20260827210000_protocol_reopen_indefinite.sql.
  const manuallyClosedAt =
    kind === "move_in"
      ? booking?.move_in_protocol_manually_closed_at
      : booking?.move_out_protocol_manually_closed_at;
  const reopenedAt =
    kind === "move_in"
      ? booking?.move_in_protocol_reopened_at
      : booking?.move_out_protocol_reopened_at;

  /**
   * A reopen granted AFTER this protocol was filed supersedes it.
   *
   * An admin reopening a protocol is saying "this record does not stand; let
   * them do it properly" — that includes the tick's deemed-accepted
   * placeholder (which nobody filed at all), and a real recording that was
   * filed before the reopen was requested. It must NOT include a recording
   * filed in response to the reopen itself, even one backdated to the actual
   * (earlier) walkthrough date — `isSupersededByReopen` ranks by the FILING
   * moment (`recorded_at` first), not the backdated `submitted_at`, for
   * exactly that reason.
   */
  const supersededByReopen = isSupersededByReopen(protocol, reopenedAt);

  if (protocol?.status === "submitted" && !supersededByReopen) {
    return {
      kindStatus: "submitted",
      locked: false,
      submittedAt: protocol.submitted_at,
      ref: protocol.ref,
      availableFrom: null,
      windowState: windowStatus.state,
    };
  }
  if (protocol?.status === "draft") {
    // A draft that wasn't submitted before its window closed counts as
    // expired so the UI surfaces "auto-accepted" rather than offering to
    // resume something the user can't finish anyway.
    if (windowStatus.isExpired) {
      return {
        kindStatus: manuallyClosedAt ? "closed" : "expired",
        locked: false,
        submittedAt: null,
        ref: protocol.ref,
        availableFrom: null,
        windowState: windowStatus.state,
      };
    }
    return {
      kindStatus: "draft",
      locked: false,
      submittedAt: null,
      ref: protocol.ref,
      availableFrom: null,
      windowState: windowStatus.state,
    };
  }

  // No protocol yet.
  if (windowStatus.isExpired) {
    // Window has already closed without a submission — either an admin
    // manually closed a reopen (neutral "closed"), or it lapsed on its own
    // and is deemed auto-accepted/auto-completed ("expired").
    return {
      kindStatus: manuallyClosedAt ? "closed" : "expired",
      locked: false,
      submittedAt: null,
      ref: null,
      availableFrom: null,
      windowState: windowStatus.state,
    };
  }
  return {
    kindStatus: "not_started",
    locked: windowStatus.state === "locked_before",
    submittedAt: null,
    ref: null,
    availableFrom: windowStatus.availableFrom,
    windowState: windowStatus.state,
  };
}

export function pickLatest(
  protocols: Protocol[],
  kind: ProtocolKind,
  /** `bookings.move_{in,out}_protocol_reopened_at` for this kind. */
  reopenedAt?: string | null
): Protocol | null {
  const matches = protocols.filter((p) => p.kind === kind);
  if (!matches.length) return null;

  // A reopen granted AFTER a submission supersedes it — the stale row stops
  // deciding this card and the tenant gets their walkthrough back. See
  // `isSupersededByReopen` for why this compares the filing moment rather
  // than `submitted_at`.
  const live = matches.filter((p) => !isSupersededByReopen(p, reopenedAt));
  // Everything for this kind predates the reopen — the tenant starts fresh.
  if (!live.length) return null;

  // Prefer a submitted one — the tenant's own first, then a staff recording;
  // otherwise the newest draft.
  //
  // A protocol an admin recorded on the tenant's behalf counts as the
  // submission for its kind, so the card reads exactly as it would for one the
  // tenant walked through themselves: "Submitted on <date>", the reference,
  // and the PDF.
  //
  // The ordering only bites when the tenant ALSO has an unfinished draft. That
  // draft used to win the card — "Resume", their window still open, the
  // recording demoted to a hint — even though a PDF for that handover was
  // already filed in their documents. Offering someone a walkthrough for a
  // handover that is already documented is the confusing half-state; the
  // consequence, that this kind's entry closes, is the point rather than a
  // side effect. It does not reopen an expired window either way — the 48h
  // move-in and 14-day move-out windows below still decide that.
  //
  // Their OWN submission still wins over a recording when both exist.
  //
  // The tick's deemed-accepted placeholder is excluded from "own": it is not
  // adminRecorded either, so it used to satisfy this find() and pose as the
  // tenant's walkthrough. It now ranks last of the submitted rows, which is
  // what lets a tenant who was let back in after an auto-close file the real
  // thing and have every card speak for THAT one — the placeholder stays on
  // file as the record of what happened, it just stops being the answer.
  const own = live.find((p) => isRealSubmission(p) && !isAdminRecorded(p));
  if (own) return own;
  const recorded = live.find((p) => isRealSubmission(p));
  if (recorded) return recorded;
  const autoSubmitted = live.find((p) => p.status === "submitted");
  if (autoSubmitted) return autoSubmitted;
  return live[0];
}

export type ProtocolPrimary = {
  kind: ProtocolKind;
  /** The draft to resume; absent when a new protocol is to be started. */
  resume?: Protocol | null;
};

/**
 * What the single primary CTA on the entry card does. Priority order:
 *   1. Resume an in-progress draft (only while its window is still open)
 *   2. Start the next not-started kind (move-in first, then move-out)
 *
 * `null` means there is nothing the tenant can do right now — every kind is
 * submitted, expired, closed, or not yet open.
 */
export function computeProtocolPrimary(args: {
  moveInState: KindState;
  moveOutState: KindState;
  moveIn: Protocol | null;
  moveOut: Protocol | null;
}): ProtocolPrimary | null {
  const { moveInState, moveOutState, moveIn, moveOut } = args;
  if (moveInState.kindStatus === "draft")
    return { kind: "move_in", resume: moveIn };
  if (moveOutState.kindStatus === "draft")
    return { kind: "move_out", resume: moveOut };
  if (moveInState.kindStatus === "not_started" && !moveInState.locked)
    return { kind: "move_in" };
  if (moveOutState.kindStatus === "not_started" && !moveOutState.locked)
    return { kind: "move_out" };
  return null;
}

export type ProtocolActionReason =
  /** A draft exists and its window is open — finish it. */
  | "resume"
  /** The window is open and nothing has been started. */
  | "start"
  /**
   * The nag lead time has begun but the window has not opened yet (move-in
   * only: the day-before email is out, the form opens on the move-in day).
   */
  | "upcoming";

export type ProtocolAction = {
  kind: ProtocolKind;
  reason: ProtocolActionReason;
  /** ISO instant the form opens, when `reason` is "upcoming". */
  availableFrom: string | null;
};

function ymd(dateStr: string): [number, number, number] {
  return dateStr.split("-").map(Number) as [number, number, number];
}

/**
 * Berlin start-of-day (approximated as fixed UTC+1, like the move-out anchors
 * in `protocol-window.ts`) of `leadDays` before a YYYY-MM-DD date. An hour
 * either way across DST is immaterial for a "from the day before" nag.
 */
function berlinStartOfDayBefore(dateStr: string, leadDays: number): Date {
  const [y, m, d] = ymd(dateStr);
  return new Date(Date.UTC(y, m - 1, d - leadDays - 1, 23, 0, 0));
}

/** The instant the move-in pip switches on: start of the day before move-in. */
export function moveInAlertStart(moveInDate: string): Date {
  return berlinStartOfDayBefore(moveInDate, MOVE_IN_ALERT_LEAD_DAYS);
}

/** The instant the move-out pip switches on: start of the day seven days out. */
export function moveOutAlertStart(moveOutDate: string): Date {
  return berlinStartOfDayBefore(moveOutDate, MOVE_OUT_ALERT_LEAD_DAYS);
}

/**
 * Is there a move-out to hand a room over at?
 *
 * One predicate, because two surfaces ask it and they were answering
 * differently (Neel, 26 Oct): the hero nagged "fill in your move-out report
 * before you leave" while the report row refused to open, on the same
 * booking, in the same week.
 *
 * Both halves matter:
 *
 * - A DATE has to exist. `computeMoveOutWindowStatus` answers "open" for a
 *   booking with no move-out date — no `opensAt` to be before, no `closesAt`
 *   to be past — which is what offered "Start report" to a tenant who had not
 *   moved in yet (#2039).
 * - `move_out_confirmed` is only disqualifying when it is explicitly `false`,
 *   which is the flag's documented meaning: false says this date is the
 *   contract's `end_date` borrowed as a fallback. `null`/`undefined` means
 *   nobody recorded either way, and the convention everywhere else in this
 *   module is to trust the date. Reading it as `!!confirmed` is what made the
 *   row stricter than the hero.
 */
export function isMoveOutScheduled(
  booking: ProtocolBookingLite | null | undefined
): boolean {
  if (!String(booking?.move_out_date ?? "").trim()) return false;
  return booking?.move_out_confirmed !== false;
}

function kindActionNeeded(
  kind: ProtocolKind,
  state: KindState,
  dateStr: string | null | undefined,
  now: number
): ProtocolAction | null {
  // Something the tenant already started and can still finish always counts,
  // regardless of lead time — they opened it themselves.
  if (state.kindStatus === "draft")
    return { kind, reason: "resume", availableFrom: null };
  if (state.kindStatus !== "not_started") return null;
  // An admin reopening the window is an explicit request for exactly this
  // walkthrough; no lead-time gate applies.
  if (state.windowState === "reopened")
    return { kind, reason: "start", availableFrom: null };
  // No date means no email would have gone out either — nothing to mirror.
  if (!dateStr) return null;
  const alertFrom =
    kind === "move_in" ? moveInAlertStart(dateStr) : moveOutAlertStart(dateStr);
  if (now < alertFrom.getTime()) return null;
  return {
    kind,
    reason: state.locked ? "upcoming" : "start",
    availableFrom: state.locked ? state.availableFrom : null,
  };
}

/**
 * Does the tenant have a handover protocol to deal with right now?
 *
 * Returns the one kind to point at (move-in outranks move-out, matching the
 * primary CTA), or `null` when nothing is actionable. Resolves — the pip
 * disappears — once the kind is submitted (by the tenant, or recorded by an
 * admin), and also once its window has expired or been closed: a pulsing pip
 * that leads to a locked card would be noise, not a nudge. The tick's
 * auto-submitted placeholder counts as resolved for the same reason, except
 * when an admin has reopened the window, which asks for the real thing again.
 */
export function computeProtocolActionNeeded(args: {
  booking: ProtocolBookingLite | null | undefined;
  protocols: Protocol[];
  now?: number;
}): ProtocolAction | null {
  const { booking, protocols } = args;
  if (!booking) return null;
  const now = args.now ?? Date.now();

  const moveIn = pickLatest(
    protocols,
    "move_in",
    booking.move_in_protocol_reopened_at
  );
  const moveOut = pickLatest(
    protocols,
    "move_out",
    booking.move_out_protocol_reopened_at
  );
  const moveInState = computeKindState(moveIn, booking, "move_in", now);
  const moveOutState = computeKindState(moveOut, booking, "move_out", now);

  const moveInAction = kindActionNeeded(
    "move_in",
    moveInState,
    booking.move_in_date,
    now
  );
  if (moveInAction) return moveInAction;

  // Only a confirmed move-out date arms the move-out nag — see
  // `ProtocolBookingLite.move_out_confirmed`. Two things still count with an
  // unconfirmed date: a draft the tenant opened themselves, and a window an
  // admin explicitly reopened — that is a direct request for the walkthrough.
  const moveOutAction = kindActionNeeded(
    "move_out",
    moveOutState,
    booking.move_out_date,
    now
  );
  if (
    moveOutAction &&
    moveOutAction.reason !== "resume" &&
    moveOutState.windowState !== "reopened" &&
    !isMoveOutScheduled(booking)
  ) {
    return null;
  }
  return moveOutAction;
}
